import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon.jsx';

/**
 * Auto-advancing banner for the patient home screen.
 *
 * Each slide is a real service from the catalogue, so tapping through lands on
 * a search already filtered to it — the banner is a shortcut into the product,
 * not decoration.
 *
 * Artwork is drawn as SVG rather than loaded as photography: the app is an
 * installable PWA that has to render offline from a precached shell, and a
 * remote image would leave a hole in the hero on a cold or flaky connection.
 * Vector scenes also re-colour with the theme and cost a couple of KB.
 *
 * Autoplay is paused on hover, on keyboard focus, and while the tab is hidden,
 * and is disabled outright for `prefers-reduced-motion` — an unstoppable moving
 * banner is one of the more common accessibility complaints.
 */

const INTERVAL_MS = 6000;

/* ------------------------------------------------------------------ Artwork */
/* Wide scenes on a 320×240 canvas. Stroke and fill are translucent white so a
   single set of drawings sits correctly on every slide gradient. */

const L = 'rgba(255,255,255,0.92)'; // primary line
const F = 'rgba(255,255,255,0.16)'; // soft fill
const D = 'rgba(255,255,255,0.42)'; // detail line

const ART = {
  recovery: (
    <>
      <circle cx="238" cy="70" r="52" fill={F} />
      <path d="M40 168h240" stroke={L} strokeWidth="4" strokeLinecap="round" />
      <path d="M56 168v-34a14 14 0 0 1 14-14h132a24 24 0 0 1 24 24v24" fill={F} stroke={L} strokeWidth="4" strokeLinejoin="round" />
      <path d="M56 148h114" stroke={D} strokeWidth="3" />
      <circle cx="92" cy="106" r="15" fill={F} stroke={L} strokeWidth="4" />
      <path d="M40 168v20M280 168v20" stroke={L} strokeWidth="4" strokeLinecap="round" />
      <path d="M150 62h22l10-18 12 34 9-16h30" stroke={L} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </>
  ),
  elder: (
    <>
      <circle cx="80" cy="78" r="54" fill={F} />
      <circle cx="112" cy="62" r="17" fill={F} stroke={L} strokeWidth="4" />
      <path d="M112 80v52M112 96l-24 14M112 96l24 12M112 132l-16 44M112 132l18 44" stroke={L} strokeWidth="4" strokeLinecap="round" />
      <circle cx="196" cy="70" r="15" fill={F} stroke={L} strokeWidth="4" />
      <path d="M196 86v46M196 100l-20 12M196 100l18 10M196 132l-14 42M196 132l16 42" stroke={D} strokeWidth="4" strokeLinecap="round" />
      <path d="M232 104v72" stroke={L} strokeWidth="4" strokeLinecap="round" />
      <path d="M226 104h12" stroke={L} strokeWidth="4" strokeLinecap="round" />
      <path d="M50 190h236" stroke={L} strokeWidth="4" strokeLinecap="round" />
    </>
  ),
  baby: (
    <>
      <circle cx="230" cy="82" r="56" fill={F} />
      <path d="M74 176v-62a34 34 0 0 1 34-34h74a34 34 0 0 1 34 34v62" fill={F} stroke={L} strokeWidth="4" strokeLinejoin="round" />
      <path d="M104 80v96M134 80v96M164 80v96M194 80v96" stroke={D} strokeWidth="3" />
      <path d="M62 176h176" stroke={L} strokeWidth="4" strokeLinecap="round" />
      <path d="M242 40c-8-9-22-9-30 0-7 8-6 19 2 27l13 13 13-13c8-8 9-19 2-27Z" fill={L} opacity="0.85" />
      <circle cx="150" cy="126" r="16" fill={F} stroke={L} strokeWidth="3.5" />
    </>
  ),
  wound: (
    <>
      <circle cx="228" cy="76" r="52" fill={F} />
      <path d="M74 150c-14-14-14-38 0-52l38-38c14-14 38-14 52 0s14 38 0 52l-38 38c-14 14-38 14-52 0Z" fill={F} stroke={L} strokeWidth="4" />
      <path d="M96 128 152 72" stroke={L} strokeWidth="4" strokeLinecap="round" />
      <circle cx="112" cy="112" r="3.5" fill={L} />
      <circle cx="128" cy="96" r="3.5" fill={L} />
      <circle cx="120" cy="126" r="3.5" fill={L} />
      <circle cx="136" cy="110" r="3.5" fill={L} />
      <path d="M212 132v44M190 154h44" stroke={L} strokeWidth="5" strokeLinecap="round" />
      <circle cx="212" cy="154" r="34" stroke={D} strokeWidth="3.5" fill="none" />
    </>
  ),
  iv: (
    <>
      <circle cx="86" cy="80" r="50" fill={F} />
      <path d="M212 44v144" stroke={L} strokeWidth="4" strokeLinecap="round" />
      <path d="M188 188h48" stroke={L} strokeWidth="4" strokeLinecap="round" />
      <path d="M212 52h28" stroke={L} strokeWidth="4" strokeLinecap="round" />
      <path d="M228 56h30a6 6 0 0 1 6 6v40a6 6 0 0 1-6 6h-30a6 6 0 0 1-6-6V62a6 6 0 0 1 6-6Z" fill={F} stroke={L} strokeWidth="4" />
      <path d="M232 74h22M232 88h14" stroke={D} strokeWidth="3" strokeLinecap="round" />
      <path d="M243 108v26" stroke={D} strokeWidth="3" strokeLinecap="round" strokeDasharray="4 7" />
      <path d="M243 142c-6-8-6-14 0-20 6 6 6 12 0 20Z" fill={L} />
      <path d="M62 150h84a16 16 0 0 0 16-16" stroke={L} strokeWidth="4" strokeLinecap="round" fill="none" />
      <path d="M54 138h20a10 10 0 0 1 0 24H54a12 12 0 0 1 0-24Z" fill={F} stroke={L} strokeWidth="4" />
    </>
  ),
};

/* ------------------------------------------------------------------- Slides */
/* Keyed by service *slug*, not id: an administrator can delete and re-add a
   category and the ids move, but the slug is derived from the name. A slide
   whose service is missing from the catalogue is dropped rather than linking to
   a search that would return nothing. */

const SLIDES = [
  {
    slug: 'post-surgery-care',
    photo: '/banner/post-surgery.jpg',
    alt: 'A nurse attending to a patient resting in bed at home',
    art: 'recovery',
    theme: 'a',
    eyebrow: 'Recovering at home',
    title: 'Post-surgery care, at your bedside',
    body: 'Recovery monitoring, drain care and mobility support after an operation — without the trip back to hospital.',
  },
  {
    slug: 'elderly-care',
    photo: '/banner/elderly.jpg',
    alt: 'A nurse holding the hand of an elderly man',
    art: 'elder',
    theme: 'b',
    eyebrow: 'For your parents',
    title: 'Elderly care, with patience and dignity',
    body: 'Daily living support, mobility help and companionship for seniors, from nurses who do this every day.',
  },
  {
    slug: 'baby-mother-care',
    photo: '/banner/baby.jpg',
    alt: 'A mother holding her newborn baby',
    art: 'baby',
    theme: 'c',
    eyebrow: 'New parents',
    title: 'Baby & mother care, day or night',
    body: 'Postnatal care, lactation support and newborn hygiene in the first weeks, when it matters most.',
  },
  {
    slug: 'wound-dressing',
    photo: '/banner/wound.jpg',
    alt: 'A nurse wrapping a bandage around a patient\'s arm',
    art: 'wound',
    theme: 'd',
    eyebrow: 'Sterile and safe',
    title: 'Wound dressing without the clinic queue',
    body: 'Sterile dressing changes for surgical, diabetic and pressure wounds, done properly at your table.',
  },
  {
    slug: 'iv-therapy',
    photo: '/banner/iv.jpg',
    alt: 'A patient receiving intravenous therapy',
    art: 'iv',
    theme: 'e',
    eyebrow: 'Under prescription',
    title: 'IV therapy in your own room',
    body: 'Cannulation, IV fluids and antibiotic infusions administered by a licence-verified nurse.',
  },
];

/**
 * `services` is the catalogue the home screen already fetched — passing it in
 * avoids a second request. Until it arrives the banner still renders, with the
 * CTA pointing at an unfiltered search.
 */
export default function ServiceBanner({ services = [] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const [failed, setFailed] = useState({});

  // Resolve each slide to a live service. Before the catalogue loads nothing is
  // dropped, so the banner never flickers from five slides down to four.
  const slides = useMemo(() => {
    if (!services.length) return SLIDES.map((s) => ({ ...s, href: '/search' }));
    const bySlug = new Map(services.map((s) => [s.slug, s]));
    return SLIDES.filter((s) => bySlug.has(s.slug)).map((s) => ({
      ...s,
      href: `/search?serviceId=${bySlug.get(s.slug).id}`,
    }));
  }, [services]);

  const count = slides.length;

  // A shrinking catalogue must not strand the index past the last slide.
  useEffect(() => {
    if (count && index >= count) setIndex(0);
  }, [count, index]);

  const go = useCallback((next) => setIndex(((next % count) + count) % count), [count]);

  // Autoplay: skipped entirely when the user has asked for reduced motion, and
  // suspended whenever the banner is hovered, focused, or off-screen.
  useEffect(() => {
    if (paused) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    const tick = () => setIndex((i) => (i + 1) % count);
    let timer = setInterval(tick, INTERVAL_MS);

    const onVisibility = () => {
      clearInterval(timer);
      if (document.visibilityState === 'visible') timer = setInterval(tick, INTERVAL_MS);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [paused, count]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(index + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(index - 1);
    }
  };

  if (!count) return null;

  return (
    <section
      className="svc-banner"
      aria-roledescription="carousel"
      aria-label="Featured care services"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={onKeyDown}
      onTouchStart={(e) => {
        touchX.current = e.touches[0].clientX;
        setPaused(true);
      }}
      onTouchEnd={(e) => {
        const dx = touchX.current == null ? 0 : e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 45) go(index + (dx < 0 ? 1 : -1));
        touchX.current = null;
        setPaused(false);
      }}
    >
      <div className="svc-banner-track" style={{ transform: `translate3d(-${index * 100}%, 0, 0)` }}>
        {slides.map((s, i) => {
          const active = i === index;
          return (
            <article
              key={s.slug}
              className="svc-slide"
              data-theme={s.theme}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${count}: ${s.title}`}
              aria-hidden={!active}
            >
              <div className="svc-slide-copy">
                <span className="svc-eyebrow">
                  <Icon name="sparkles" />
                  {s.eyebrow}
                </span>
                <h2>{s.title}</h2>
                <p>{s.body}</p>
                <Link
                  to={s.href}
                  className="btn btn-lg svc-cta"
                  tabIndex={active ? 0 : -1}
                >
                  Find a nurse <Icon name="arrow-right" />
                </Link>
              </div>
              <div className="svc-slide-art">
                {failed[s.slug] ? (
                  // The photograph is a network resource; the vector scene is
                  // precached with the shell, so it stands in when offline.
                  <svg viewBox="0 0 320 240" focusable="false" aria-hidden="true">{ART[s.art]}</svg>
                ) : (
                  <img
                    src={s.photo}
                    alt={s.alt}
                    width="1000"
                    height="666"
                    loading={i === 0 ? 'eager' : 'lazy'}
                    fetchPriority={i === 0 ? 'high' : 'low'}
                    decoding="async"
                    onError={() => setFailed((f) => ({ ...f, [s.slug]: true }))}
                  />
                )}
              </div>
            </article>
          );
        })}
      </div>

      <div className="svc-banner-controls">
        <button type="button" className="svc-arrow" onClick={() => go(index - 1)} aria-label="Previous slide">
          <Icon name="chevron-left" />
        </button>
        <div className="svc-dots" role="tablist" aria-label="Choose a slide">
          {slides.map((s, i) => (
            <button
              key={s.slug}
              type="button"
              role="tab"
              className="svc-dot"
              aria-selected={i === index}
              aria-label={`Slide ${i + 1}: ${s.title}`}
              data-active={i === index}
              onClick={() => go(i)}
            >
              <span style={paused || i !== index ? { animation: 'none' } : undefined} />
            </button>
          ))}
        </div>
        <button type="button" className="svc-arrow" onClick={() => go(index + 1)} aria-label="Next slide">
          <Icon name="chevron-right" />
        </button>
      </div>
    </section>
  );
}
